// Der Speicher am Gerät für die Besichtigung (E2; PLAN.md, Abschnitt 3,
// „Offline zuerst“): IndexedDB über Dexie, eigene Datenbank „he-doku“. Die
// Proben-Datenbank „he-doku-proben“ aus E1 räumt der Start seit E2,
// Schritt 6 weg (`probenSpeicherWegraeumen`) – die Proben gibt es nicht mehr.
//
// Hier liegt die Arbeitskopie – alles, was der Verkäufer aufnimmt, sofort und
// auch ohne Empfang – und der Postausgang, der sich merkt, was davon noch an
// den Server muss (postausgang.ts).
//
// Das Schema steht schon für ganz E2 (Fotos, Memos, K2, Nachträge): Jede
// spätere Änderung bräuchte ein Versions-Upgrade am Gerät. Die Kennungen
// entstehen am Gerät (`neueKennung`) und sind dieselben wie am Server.
// Nach der Bestätigung durch den Server gibt die App nur die Dateien frei;
// Texte und Längen bleiben als Kopie zum Nachsehen (seit E2, Schritt 4:
// `dateienFreigeben` in dateien.ts; Fotos und Memos holt die App dann bei
// Bedarf vom Server, nur in den Arbeitsspeicher).
//
// Version 2 (19.10.2026): Fotos und Sprachmemos liegen nicht mehr als Blob
// an ihrem Datensatz, sondern als ArrayBuffer in der eigenen Tabelle
// „dateien“, benannt nach Prüfsumme, einmal geschrieben und nie
// überschrieben (dateien.ts). Warum: Safari macht einen Blob aus IndexedDB
// unlesbar, wenn er nach einem Neustart der App gelesen und wieder
// gespeichert wird (WebKit-Fehler 235687, „WebKitBlobResource error 1“) –
// und jede Änderung am Foto (Zeichnung, „hochgeladen“, „abgeschickt“)
// speichert den ganzen Datensatz neu. Augentest am iPhone: ein Foto zeigte
// danach nur ein Fragezeichen. Das K2-PDF lag schon immer als ArrayBuffer
// (Plan E2). Alte Blobs zieht `dateienUmziehen` beim Start um.
//
// Seit E3 (S6, S7): Fragen und Montagetermin liegen als Felder an der
// Besichtigung (`Aufnahme`) – was der Server zuletzt sagte und was das Gerät
// dazu gesagt hat (gespraech.ts). Freiwillig und ohne Index, wie
// `vermisstSeit`: keine neue Fassung des Speichers nötig. Warum an der
// Besichtigung und nicht in einer eigenen Tabelle: Gelesen werden sie immer
// mit ihr, sie sind wenig Text und tragen keine Blobs – ein Neu-Speichern des
// Datensatzes schadet also nicht (anders als bei Fotos, siehe oben).
//
// Version 3 (seit E3b, O65 – Material draußen): zwei neue Tabellen, an den
// alten ändert sich nichts. „materialangaben“ – was der Verkäufer an
// Material angibt, ein Stück der Besichtigung wie die Längen; und
// „standardliste“ – was er antippen kann, vom Server geholt und hier
// aufbewahrt, damit „Material“ auch im Keller ohne Netz geht. Welche
// Tabellen zu den Stücken einer Besichtigung gehören, steht seitdem an EINER
// Stelle (`stueckeTabellen`) – fehlte eine in einer Transaktion, bliebe
// jeder Abschluss hängen (Prüfung des Plans E3b, Befund 2).
//
// Version 4 (seit E3c, S5): Die Dachplanung mit K2 bekommt einen zweiten
// Platz – den Excel-Report von K2 (`K2.excel`, die Liste für den
// Lieferanten); das Upgrade setzt ihn an jeder schon abgelegten Dachplanung
// auf null, so liest keine Stelle ein fehlendes Feld. Dazu die Tabelle
// „firma“: was die Firma nach draußen gibt und am Gerät liegen muss, damit es
// ohne Netz geht – die Adresse von K2 für „Dachplanung starten“ – und für
// welche Firma die Standardliste und diese Werte geholt wurden (O86: meldet
// sich jemand einer anderen Firma an, oder meldet er sich ab, kommen sie weg).
// Höhe und Zugang am Dach stehen an der Station (`Station.fachfragen`),
// freiwillig und ohne Index.
//
// Version 5 (seit E3c, S6 – Löschen): die Tabelle „hinweise“ – was die App
// dem Verkäufer einmal sagen muss, auch wenn er die Seite gerade nicht offen
// hat: „Das Büro hat diese Besichtigung gelöscht“ (wegraeumen.ts). Die Kopie
// ist dann schon weg; der Hinweis bleibt, bis er „Verstanden“ tippt. An den
// alten Tabellen ändert sich nichts.

import Dexie, { type EntityTable } from "dexie";
import type {
  DachFakten,
  DraussenMaterialZeile,
  EintragArt,
  FrageAmServer,
  FrageBezug,
  FrageStand,
  LoeschbareTabelle,
  MemoFormat,
  Montagetermin,
  NutzerKurz,
  Zeichnung,
} from "@happy-energy/kern";

/** Wo eine Besichtigung am Gerät steht. */
export type AufnahmeStand =
  /** Wird gerade aufgenommen – alles lässt sich ändern. */
  | "in_aufnahme"
  /** „An das Büro senden“ getippt, der Server hat noch nicht alles bestätigt (Schritt 4). */
  | "abgeschlossen"
  /** Der Server hat bestätigt: alles da. */
  | "im_buero";

export interface Aufnahme {
  /** Die Kennung der Aufnahme – zugleich die Kennung des Eintrags „aufnahme“. */
  id: string;
  /**
   * `name` zeigen alle Listen. `vorname`, `nachname` seit E3b (O82) – fehlen
   * an Besichtigungen von vorher (gilt als nicht aufgeteilt; ohne Index,
   * keine neue Fassung des Speichers).
   */
  kunde: { id: string; art: "person"; name: string; telefon: string | null; email: string | null; vorname?: string | null; nachname?: string | null };
  objekt: { id: string; art: "einfamilienhaus"; strasse: string; plz: string; ort: string };
  anlage: { id: string; produktlinie: "sun" };
  begonnenAm: string;
  stand: AufnahmeStand;
  /** Ein dauerhafter Fehler beim Senden – sichtbar an der Besichtigung; null, solange alles gut geht. */
  fehler: string | null;
  /** Wann „An das Büro senden“ getippt wurde (Schritt 4). */
  abgeschlossenAm: string | null;
  /** Wann der Server „alles da“ bestätigt hat (Schritt 4). */
  imBueroAm: string | null;
  /**
   * Seit wann der Server diese Besichtigung nicht mehr in der Liste
   * „Gesendet“ nennt (einsicht.ts) – gelöscht wird die Kopie erst, wenn sie
   * drei Tage lang fehlt; taucht sie wieder auf, ist die Marke weg. Leer:
   * in der Liste. Seit 22.10.2026 (Code-Prüfung E2, Befund 2), freiwillig
   * und ohne Index – keine neue Fassung des Speichers nötig.
   */
  vermisstSeit?: string | null;

  // --- Seit E3 (S6, S7): Fragen und Montagetermin (gespraech.ts) ----------------------
  // Alle freiwillig und ohne Index. „Vom Server“ ersetzt jedes Auffrischen
  // (einsicht.ts); „vom Gerät“ bleibt, bis die Kopie es sicher kennt.

  /** Vom Server: die Fragen an dieser Besichtigung samt Antworten, älteste zuerst. Fehlt: noch nie geholt. */
  fragen?: FrageAmServer[];
  /** Wann diese Fragen erfragt wurden – Zeit des Geräts, kurz vor der Anfrage. */
  fragenAm?: string;
  /** Wie viele davon dabei offen waren – merkt der Abgleich, ob sich an der Liste „Gesendet“ etwas geändert hat. */
  offeneFragenAmServer?: number;
  /** Vom Server: der Montagetermin, mit dem Kunden vereinbart. */
  montagetermin?: Montagetermin;
  /** Vom Gerät: Fragen, die der Verkäufer gestellt hat. */
  eigeneFragen?: EigeneFrage[];
  /** Vom Gerät: seine Antworten. */
  eigeneAntworten?: EigeneAntwort[];
  /** Vom Gerät: „erledigt“ oder wieder „offen“ – je Frage höchstens einer, der jüngste. */
  eigeneStaende?: EigenerStand[];
  /** Vom Gerät: der Montagetermin, zuletzt gespeichert. */
  eigenerTermin?: EigenerTermin | null;
  /**
   * Der Montagetermin, wie er gerade getippt wird – noch nicht gespeichert.
   * Jede Änderung liegt sofort hier, damit sie den Wechsel in den Kalender
   * überlebt (Gernots Entscheidung 4). „Speichern“ leert ihn.
   */
  terminEntwurf?: TerminEntwurf | null;
}

/**
 * Was das Gerät zu den Fragen sagt – mit dem Zeitpunkt, an dem der Server
 * „ok“ sagte (Zeit des Geräts; null, solange es wartet). Daran erkennt das
 * Auffrischen, ob die Kopie vom Server es schon enthält (gespraech.ts).
 */
export interface VomGeraet {
  bestaetigtAm: string | null;
}

export interface EigeneFrage extends VomGeraet {
  id: string;
  bezug: { tabelle: FrageBezug; id: string };
  /** Getrimmt – so, wie der Server sie bekommt und vergleicht. */
  text: string;
  gestelltAm: string;
  von: NutzerKurz;
}

export interface EigeneAntwort extends VomGeraet {
  id: string;
  frageId: string;
  text: string;
  geantwortetAm: string;
  von: NutzerKurz;
}

export interface EigenerStand extends VomGeraet {
  frageId: string;
  stand: FrageStand;
  am: string;
  von: NutzerKurz;
}

export interface EigenerTermin extends VomGeraet {
  /** „JJJJ-MM-TT“ – beides null: kein Termin. */
  am: string | null;
  text: string | null;
  geaendertAm: string;
}

/** So, wie getippt: der Tag aus dem Datumsfeld („“ oder „JJJJ-MM-TT“) und der Text. */
export interface TerminEntwurf {
  am: string;
  text: string;
}

export interface Station {
  id: string;
  aufnahmeId: string;
  /** Schlüssel aus daten/stationen.json, z. B. „verteiler“. */
  art: string;
  nr: number;
  fertig: boolean;
  /** „Sonstiges“ – so, wie getippt; leer gilt als nicht angegeben. */
  sonstiges: string | null;
  /** Zuletzt in diesem Nachtrag berichtigt (Schritt 5). */
  geaendertImNachtrag: string | null;
  /**
   * Seit E3c (S5): die Gegebenheiten am Dach (Kern, dach-fakten.ts) – nur an
   * der Station „dach“; fehlt oder null: nichts erfasst. Im Nachtrag
   * berichtigt wie „Sonstiges“ (dieselbe Marke `geaendertImNachtrag`).
   */
  fachfragen?: DachFakten | null;
}

/** Die Notiz zur ganzen Station oder zu einem Foto – genau eines von beiden. */
export interface Notiz {
  id: string;
  aufnahmeId: string;
  stationId: string | null;
  fotoId: string | null;
  text: string;
  nachtragId: string | null;
}

export interface Laenge {
  id: string;
  aufnahmeId: string;
  stationId: string;
  /** Schlüssel aus daten/laengen-arten.json */
  laengenArt: string;
  /** null: geleert – die Kennung bleibt, am Server wird sie gelöscht. */
  meter: number | null;
  anmerkung: string | null;
  /** Mit welcher Sendung sie kam: null die erste, sonst dieser Nachtrag. */
  nachtragId: string | null;
  /**
   * Zuletzt in diesem Nachtrag berichtigt (seit Schritt 5) – wie bei der
   * Station. Sie gehört weiter zu ihrer Sendung (`nachtragId` bleibt). Fehlt
   * in Daten von vor Schritt 5 (gilt als null; ohne Index, keine neue Fassung).
   */
  geaendertImNachtrag?: string | null;
}

/**
 * Material, das der Verkäufer angibt (seit E3b, O65) – auf der Seite
 * „Material“ (`stationId` null) oder über den Knopf in einer Station.
 * Angetippt aus der Standardliste (`standardId`) oder „Etwas anderes“
 * (null). Bezeichnung, Einheit und Artikelnummer sind Kopien; die Menge ist
 * immer eine Zahl – „weg“ heißt hier: der Datensatz ist weg (und „geloescht“
 * geht hinaus).
 */
export interface Materialangabe {
  id: string;
  aufnahmeId: string;
  stationId: string | null;
  standardId: string | null;
  artikelnummer: string | null;
  bezeichnung: string;
  menge: number;
  einheit: string;
  /** Mit welcher Sendung es kam: null die erste, sonst dieser Nachtrag. */
  nachtragId: string | null;
  /** Zuletzt in diesem Nachtrag in der Menge berichtigt – wie bei der Länge. */
  geaendertImNachtrag: string | null;
  /** Die Reihenfolge, in der es dazukam (ohne Index – sortiert wird am Gerät). */
  nr: number;
}

/** Ein Eintrag der Standardliste, wie der Server ihn für draußen liefert – am Gerät aufbewahrt. */
export type StandardEintrag = DraussenMaterialZeile;

export interface Foto {
  id: string;
  aufnahmeId: string;
  stationId: string;
  reihenfolge: number;
  /**
   * Nur noch in Daten von vor Version 2: das JPEG als Blob. Seit 19.10.2026
   * liegt die Datei in „dateien“ (nach Prüfsumme); beim Start zieht sie um
   * und das Feld wird null.
   */
  datei?: Blob | null;
  pruefsumme: string;
  breite: number;
  hoehe: number;
  groesse: number;
  /** Vor dem Verkleinern ausgelesen; null, wenn das Foto keine Zeit trug. */
  aufnahmezeit: string | null;
  zeichnung: Zeichnung | null;
  nachtragId: string | null;
  /** Die Datei ist am Server (Datei-Eingang hat bestätigt). */
  hochgeladen: boolean;
  /**
   * Der Foto-Eintrag ging mindestens einmal an den Server – ob er ihn hat,
   * ist ab dann ungewiss (die Antwort kann im Funkloch verloren gehen).
   * Gesetzt, BEVOR er hinausgeht. Ein Foto, das nie hinausging, löscht die
   * App nur am Gerät; sonst auch am Server (seit E2, Schritt 2).
   */
  abgeschickt?: boolean;
}

export interface Memo {
  id: string;
  aufnahmeId: string;
  notizId: string;
  /** Nur noch in Daten von vor Version 2 – wie beim Foto; die Datei liegt jetzt in „dateien“. */
  datei?: Blob | null;
  /** Steht fest, sobald die Aufnahme beendet ist – null heißt: Die Aufnahme läuft (oder brach ab, memosRetten). */
  pruefsumme: string | null;
  format: MemoFormat;
  /** Am Gerät gemessen – Safari schreibt keine Dauer in die MP4. */
  dauerSekunden: number;
  aufgenommenAm: string;
  nachtragId: string | null;
  hochgeladen: boolean;
  /** Wie beim Foto: Der Memo-Eintrag ging mindestens einmal hinaus – gelöscht wird es dann auch am Server (seit Schritt 3). */
  abgeschickt?: boolean;
}

/** Ein Teilstück eines Memos – jede Sekunde sofort gesichert, falls die Aufnahme abbricht. */
export interface MemoTeil {
  id?: number;
  memoId: string;
  nr: number;
  /** Seit Version 2 als ArrayBuffer – ältere Teilstücke noch als Blob. Wird nie überschrieben. */
  teil: ArrayBuffer | Blob;
  zeit: string;
  /** Die gemessene Dauer bis hierher – nach einem Abbruch die Dauer des geretteten Memos (seit Schritt 3). */
  sekunden?: number;
}

/** „Dachplanung mit K2“ – Kennung ist die der Anlage. */
export interface K2 {
  id: string;
  aufnahmeId: string;
  /** Das PDF, kopiert aus dem <input>; null bei „überspringen“ oder nach der Bestätigung. */
  pdf: ArrayBuffer | null;
  dateiname: string | null;
  groesse: number;
  pruefsumme: string | null;
  uebersprungen: boolean;
  nachtragId: string | null;
  hochgeladen: boolean;
  /**
   * Seit Version 4 (E3c, S5): der Excel-Report von K2 – null: (noch) keiner.
   * Unabhängig vom PDF: „überspringen“ nimmt ihn nicht weg.
   */
  excel: K2Excel | null;
}

/** Der Excel-Report von K2 am Gerät – wie das PDF als ArrayBuffer, nie als Blob (WebKit, oben). */
export interface K2Excel {
  /** Die .xlsx, kopiert aus dem <input>; null nach der Bestätigung (im Büro) oder in der Kopie vom Server. */
  daten: ArrayBuffer | null;
  dateiname: string | null;
  groesse: number;
  pruefsumme: string;
  hochgeladen: boolean;
}

/**
 * Was die Firma nach draußen gibt und am Gerät liegen muss (seit Version 4,
 * E3c, S5) – genau eine Zeile („firma“). `firmaId`: für welche Firma die
 * Standardliste und diese Werte geholt wurden (O86).
 */
export interface FirmaWerte {
  id: "firma";
  firmaId: string;
  /** Die Adresse von K2 für „Dachplanung starten“ – null: keine eingetragen (oder noch nie geholt). */
  k2Adresse: string | null;
}

/**
 * Ein Hinweis, den die App dem Verkäufer einmal sagt (seit Version 5, E3c,
 * S6): Das Büro hat eine Besichtigung gelöscht – die Kopie am Gerät ist
 * schon weg (wegraeumen.ts), der Hinweis steht auf der Startseite, bis er
 * „Verstanden“ tippt. Name und Adresse, damit er weiß, welche; sonst nichts.
 *
 * Seit der Code-Prüfung E3c (Befund 3) dazu „buero-arbeitet“: Der Verkäufer
 * hat eine Besichtigung verworfen, mit deren Anlage das Büro schon arbeitet –
 * am Gerät ist sie weg, im CRM bleibt sie; löschen kann sie dort nur die
 * Leitung (sender.ts). Dieselbe Tabelle, kein neuer Index – keine neue
 * Fassung des Speichers.
 */
export interface Hinweis {
  id?: number;
  art: "geloescht" | "buero-arbeitet";
  name: string;
  ort: string;
  /** Wann der Hinweis entstand (ISO). */
  wann: string;
}

/**
 * Eine Datei am Gerät – Foto oder Sprachmemo (seit Version 2): die Bytes als
 * ArrayBuffer, benannt nach ihrer Prüfsumme, mit ihrem Typ. Einmal
 * geschrieben, nie überschrieben; weg, sobald kein Foto und kein Memo sie
 * mehr braucht.
 */
export interface Datei {
  pruefsumme: string;
  daten: ArrayBuffer;
  /** „image/jpeg“, „audio/mp4“, „audio/webm“ */
  typ: string;
}

/**
 * „Etwas nachreichen“ (Schritt 5): nach dem Senden eine weitere Sendung
 * derselben Besichtigung. Je Besichtigung läuft höchstens einer – erst wenn
 * der Server ihn bestätigt hat („im_buero“), kann der nächste beginnen. Die
 * Felder nach `stand` kamen mit Schritt 5 dazu, freiwillig und ohne Index:
 * keine neue Fassung des Speichers nötig.
 */
export interface Nachtrag {
  id: string;
  aufnahmeId: string;
  begonnenAm: string;
  /**
   * offen: Es wird nachgereicht – Neues kommt dazu, Längen und „Sonstiges“
   * lassen sich berichtigen. abgeschlossen: „Nachtrag senden“ getippt, der
   * Server hat noch nicht alles bestätigt. im_buero: bestätigt.
   */
  stand: "offen" | "abgeschlossen" | "im_buero";
  /** Wann „Nachtrag senden“ getippt wurde. */
  abgeschlossenAm?: string | null;
  /** Wann der Server „alles da“ bestätigt hat. */
  imBueroAm?: string | null;
  /**
   * Der Eintrag „nachtrag“ ging mindestens einmal an den Server – wie beim
   * Foto: Ab dann ist ungewiss, ob der Server ihn hat, und „Nachtrag
   * abbrechen“ schließt ihn dort (statt ihn nur am Gerät zu vergessen).
   * Gesetzt, BEVOR er hinausgeht (sender.ts).
   */
  abgeschickt?: boolean;
}

/**
 * Was im Postausgang stehen kann: jede Art von Eintrag – und „datei“, eine
 * Datei für den Datei-Eingang (seit E2, Schritt 2). Bei „datei“ ist die
 * Kennung die Prüfsumme; sie steht in der Reihe VOR dem Eintrag, der sie
 * braucht (Foto, Memo, K2).
 */
export type PostArt = EintragArt | "datei";

/** Die Teile der Dachplanung, die ein K2-Eintrag sendet (seit E3c, S5). */
export type K2Teil = "pdf" | "excel";

/**
 * Ein Eintrag im Postausgang: Er merkt sich, WAS sich geändert hat, nicht
 * eine Kopie davon (Plan E2). Der Inhalt entsteht erst beim Senden aus der
 * Arbeitskopie. Je Sache (Art und Kennung) gibt es höchstens einen Eintrag.
 */
export interface PostEintrag {
  /**
   * Nur bei „k2“ (seit E3c, S5): was an der Dachplanung zu senden ist – das
   * PDF (oder „übersprungen“), der Excel-Report oder beides. Fehlt es (ein
   * Eintrag von vor dem Update): das PDF, wie bisher. Freiwillig, ohne Index.
   */
  k2Teile?: K2Teil[];
  /** Der Platz in der Reihe – Eltern stehen immer vor ihren Kindern. */
  nr?: number;
  art: PostArt;
  /** Die Kennung der Sache – bei „datei“ die Prüfsumme. */
  kennung: string;
  aufnahmeId: string;
  nachtragId?: string;
  /** Nur bei „geloescht“: woraus gelöscht wird. */
  tabelle?: LoeschbareTabelle;
  /**
   * Nur bei „verwerfen“ (seit der Code-Prüfung E3c, Befund 3): Name und
   * Adresse der Besichtigung, die am Gerät schon weg ist – für den Hinweis,
   * falls das Büro schon mit ihrer Anlage arbeitet. Bleibt am Gerät, geht nie
   * an den Server. Freiwillig, ohne Index.
   */
  verworfen?: { name: string; ort: string };
  /** Zählt jede Änderung – gelöscht wird nach dem Senden nur, was sich seither nicht geändert hat. */
  version: number;
  /** Fehlgeschlagene Versuche – daraus der wachsende Abstand. */
  versuche: number;
  /** Wie oft der Server „eltern-fehlt“ sagte – nach dem dritten Mal gilt das als dauerhaft. */
  elternFehlt: number;
  /**
   * Nur beim Abschluss (seit E2, Schritt 4): wie oft die App schon
   * nachgeschickt hat, was dem Server fehlte („unvollstaendig“) – nach dem
   * dritten Mal gilt das als dauerhaft. Freiwillig, ohne Index: keine neue
   * Fassung des Speichers nötig.
   */
  heilversuche?: number;
  /** Dauerhafter Fehler: Der Eintrag bleibt liegen, bis sich die Sache ändert oder „Nochmal senden“. */
  fehler: string | null;
  /** Frühestens dann wieder versuchen (Millisekunden seit 1970); 0 = sofort. */
  naechsterVersuch: number;
}

export type Speicher = Dexie & {
  aufnahmen: EntityTable<Aufnahme, "id">;
  stationen: EntityTable<Station, "id">;
  notizen: EntityTable<Notiz, "id">;
  laengen: EntityTable<Laenge, "id">;
  fotos: EntityTable<Foto, "id">;
  memos: EntityTable<Memo, "id">;
  memoTeile: EntityTable<MemoTeil, "id">;
  k2: EntityTable<K2, "id">;
  nachtraege: EntityTable<Nachtrag, "id">;
  postausgang: EntityTable<PostEintrag, "nr">;
  dateien: EntityTable<Datei, "pruefsumme">;
  materialangaben: EntityTable<Materialangabe, "id">;
  standardliste: EntityTable<StandardEintrag, "id">;
  firma: EntityTable<FirmaWerte, "id">;
  hinweise: EntityTable<Hinweis, "id">;
};

/**
 * Die Fassungen des Speichers, der Reihe nach – `bis`: nur bis zu dieser
 * (für den Test, der ein Gerät mit altem Stand das Update bekommen lässt).
 */
export function fassungenAnlegen(db: Dexie, bis = 5): void {
  // Version 1 – das ganze Schema von E2. Indizes nur, wonach gesucht wird.
  db.version(1).stores({
    aufnahmen: "id, stand, begonnenAm",
    stationen: "id, aufnahmeId, [aufnahmeId+nr]",
    notizen: "id, aufnahmeId, stationId, fotoId, nachtragId",
    laengen: "id, aufnahmeId, [stationId+laengenArt], nachtragId",
    fotos: "id, aufnahmeId, stationId, pruefsumme, nachtragId",
    memos: "id, aufnahmeId, notizId, nachtragId",
    memoTeile: "++id, memoId, [memoId+nr]",
    k2: "id, aufnahmeId",
    nachtraege: "id, aufnahmeId",
    // Eindeutig je Sache: Art und Kennung.
    postausgang: "++nr, &[art+kennung], aufnahmeId",
  });
  if (bis < 2) return;

  // Version 2 (19.10.2026) – nur eine neue Tabelle, an den alten ändert sich
  // nichts; das Umziehen alter Blobs macht `dateienUmziehen` (dateien.ts)
  // beim Start, außerhalb dieses Upgrades.
  db.version(2).stores({
    dateien: "pruefsumme",
  });
  if (bis < 3) return;

  // Version 3 (seit E3b) – nur zwei neue Tabellen, die alten bleiben, wie sie
  // sind: Ein Gerät, das mitten in einer Besichtigung das Update bekommt,
  // behält alles, auch seine Längen und was im Postausgang wartet.
  db.version(3).stores({
    materialangaben: "id, aufnahmeId, stationId, nachtragId",
    standardliste: "id",
  });
  if (bis < 4) return;

  // Version 4 (seit E3c, S5) – eine neue Tabelle, und an jeder abgelegten
  // Dachplanung der leere Platz für den Excel-Report. Das PDF daran ist ein
  // ArrayBuffer, kein Blob: Das Neu-Speichern schadet ihm nicht (oben, Version 2).
  db.version(4)
    .stores({ firma: "id" })
    .upgrade((tx) =>
      tx
        .table<K2>("k2")
        .toCollection()
        .modify((k) => {
          if (k.excel === undefined) k.excel = null;
        }),
    );
  if (bis < 5) return;

  // Version 5 (seit E3c, S6) – nur eine neue Tabelle, die alten bleiben, wie sie sind.
  db.version(5).stores({
    hinweise: "++id",
  });
}

export const speicher = new Dexie("he-doku") as Speicher;
fassungenAnlegen(speicher);

/**
 * Die Tabellen der Stücke einer Besichtigung – alles, was beim Abschluss
 * abgeglichen wird (Kern, ABGLEICH_TABELLEN). Jede Transaktion, die die
 * Stücke liest (`stueckeLesen`, Abschluss, Nachtrag, Kopie vom Server),
 * nimmt genau diese Liste.
 */
export function stueckeTabellen() {
  return [speicher.stationen, speicher.fotos, speicher.notizen, speicher.memos, speicher.laengen, speicher.materialangaben] as const;
}

/**
 * Die Datenbank der Technik-Proben aus E1 („he-doku-proben“) löschen – mit
 * den Probefotos und Memo-Stücken darin. Die Proben-Seite gibt es seit E2,
 * Schritt 6 nicht mehr; sonst lägen die Fotos für immer am Gerät. Gibt es
 * die Datenbank nicht (mehr), passiert nichts – deshalb bei jedem Start.
 */
export function probenSpeicherWegraeumen(): Promise<void> {
  return Dexie.delete("he-doku-proben");
}
